/**
 * The application frame.
 *
 * The navigation is not a fixed list. It is computed from three things:
 *
 *   1. which modules the organisation has (tenant configuration),
 *   2. which features are switched on (tenant configuration),
 *   3. what this user is permitted to do (the session, decided by the server).
 *
 * So a trust sees "Academies" where a college sees "Companies", an academy
 * business manager never sees the consolidation view, and a college that has
 * not bought curriculum planning has no planning link at all — from one build.
 *
 * Hiding a link is a courtesy. The server refuses the request regardless.
 *
 * THE TOP BAR ANSWERS "WHERE AM I?"
 * ---------------------------------
 * Four questions a finance user asks constantly, answered without a click:
 * which organisation, which entity, which financial year, which period. A
 * figure read off the wrong entity or the wrong period is worse than no figure,
 * so the answer is on screen at all times rather than a click away.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useTenant } from '../lib/tenant';
import { useAsync } from '../lib/useAsync';
import { api } from '../lib/api';
import { date } from '../lib/format';
import { DemonstrationNotice } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Any one of these permissions is enough to show the link. */
  permissions?: string[];
  module?: string;
  feature?: string;
  end?: boolean;
  /** A live count shown beside the link. */
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface PeriodContext {
  financialYear: { code: string; startDate?: string; endDate?: string } | null;
  currentPeriod: { name: string } | null;
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, signOut, switchEntity, canAny, activeEntity } = useSession();
  const { branding, terminology: t, environmentLabel, isDemonstration, moduleEnabled, featureEnabled, configuration } =
    useTenant();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [menu, setMenu] = useState<'none' | 'profile' | 'help'>('none');
  const menuRef = useRef<HTMLDivElement>(null);

  const entityId = user?.entityId ?? null;

  /*
   * Where we are in the calendar. Fetched once per entity — the shell stays
   * mounted as routes change, so moving between screens does not re-ask.
   */
  const period = useAsync<PeriodContext>(
    () =>
      entityId
        ? api.get<PeriodContext>('/dashboard').then((d) => ({
            financialYear: d.financialYear ?? null,
            currentPeriod: d.currentPeriod ?? null,
          }))
        : Promise.resolve({ financialYear: null, currentPeriod: null }),
    [entityId],
  );

  /* How many approvals are genuinely waiting for this person. Never a decoration. */
  const approvals = useAsync<{ approvals: unknown[] }>(
    () =>
      entityId && canAny('workflow.view')
        ? api.get<{ approvals: unknown[] }>('/procurement/approvals/pending')
        : Promise.resolve({ approvals: [] }),
    [entityId],
  );
  const waiting = approvals.data?.approvals?.length ?? 0;

  // Close either popover on Escape or a click outside it.
  useEffect(() => {
    if (menu === 'none') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu('none');
    };
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu('none');
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menu]);

  if (!user) return null;

  const groups: NavGroup[] = [
    {
      label: 'Overview',
      items: [
        { to: '/', label: 'Dashboard', icon: '◱', end: true },
        { to: '/approvals', label: 'My approvals', icon: '✓', permissions: ['workflow.view'], badge: waiting },
        {
          to: '/consolidation',
          label: `${t.consolidated} position`,
          icon: '◫',
          feature: 'consolidatedDashboard',
          permissions: ['report.run.all'],
        },
      ],
    },
    {
      label: 'Purchasing',
      items: [
        { to: '/requisitions', label: t.requisitionPlural, icon: '▤', permissions: ['requisition.create', 'requisition.view.all'] },
        { to: '/orders', label: t.purchaseOrderPlural, icon: '▦', permissions: ['po.view.all', 'requisition.create'] },
        { to: '/receipts', label: 'Goods receipts', icon: '▣', permissions: ['grn.record', 'po.view.all'] },
        { to: '/suppliers', label: t.supplierPlural, icon: '◈', permissions: ['supplier.view'] },
      ],
    },
    {
      label: 'Purchase ledger',
      items: [
        { to: '/invoices', label: 'Supplier invoices', icon: '▧', permissions: ['ap.view'] },
        { to: '/payments', label: 'Payment runs', icon: '⇉', permissions: ['ap.payment.prepare', 'ap.payment.approve', 'ap.view'] },
      ],
    },
    {
      label: 'Sales ledger',
      items: [
        { to: '/sales', label: 'Sales invoices', icon: '▨', permissions: ['ar.view', 'ar.request'] },
        { to: '/debtors', label: 'Credit control', icon: '◔', permissions: ['ar.view'] },
      ],
    },
    {
      label: 'Finance',
      items: [
        { to: '/ledger', label: 'General ledger', icon: '≡', permissions: ['gl.view'] },
        { to: '/cash', label: 'Cash book', icon: '◍', permissions: ['cash.view'] },
        { to: '/assets', label: 'Fixed assets', icon: '◨', permissions: ['assets.view'] },
        { to: '/projects', label: t.projectPlural, icon: '◇', permissions: ['project.view'], module: 'projectAccounting' },
      ],
    },
    {
      label: 'Planning',
      items: [
        { to: '/budgets', label: 'Budgets', icon: '◧', permissions: ['budget.view.own', 'budget.view.all'] },
        { to: '/planning', label: 'Curriculum planning', icon: '◑', permissions: ['plan.view'], module: 'icfp' },
      ],
    },
    {
      label: 'Reporting',
      items: [
        { to: '/reports', label: 'Reports', icon: '▥', permissions: ['report.run'] },
        { to: '/contracts', label: 'Contracts', icon: '◰', permissions: ['contract.view'], module: 'contractIntelligence' },
        { to: '/assistant', label: 'Finance assistant', icon: '◎', permissions: ['ai.use'] },
      ],
    },
    {
      label: 'Operations',
      items: [
        { to: '/close', label: 'Period close', icon: '◐', permissions: ['close.view'] },
        { to: '/integrations', label: 'Integrations', icon: '⇄', permissions: ['integration.view'] },
        { to: '/migration', label: 'Migration centre', icon: '⇥', permissions: ['migration.manage'] },
        { to: '/audit', label: 'Audit trail', icon: '◉', permissions: ['audit.view'] },
        { to: '/self-service', label: 'Self service', icon: '◌', permissions: ['expense.claim', 'supplier.request', 'pcard.code'] },
        { to: '/support', label: 'Support', icon: '◒', permissions: ['support.raise'] },
      ],
    },
    {
      label: 'Administration',
      items: [
        { to: '/admin', label: 'Administration', icon: '⚙', permissions: ['admin.settings', 'platform.tenant.view', 'workflow.configure'] },
      ],
    },
  ];

  const visible = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.module && !moduleEnabled(item.module)) return false;
        if (item.feature && !featureEnabled(item.feature)) return false;
        if (item.permissions && !canAny(...item.permissions)) return false;
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);

  const initials = user.fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const support = configuration?.support;
  const year = period.data?.financialYear;
  const currentPeriod = period.data?.currentPeriod;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to the main content
      </a>

      {/*
        The brand occupies its own corner of the grid, above the navigation and
        beside the top bar. It was previously nested inside the header, where
        `grid-area: brand` had no effect: the corner sat empty and the brand
        rendered as a dark island floating in a white bar.
      */}
      <div className="app-brand">
        <span className="app-brand__mark" aria-hidden="true">
          {branding.monogram}
        </span>
        <span className="app-brand__text">
          <span className="app-brand__name">{branding.productName}</span>
          <span className="app-brand__sub">{branding.productTagline}</span>
        </span>
      </div>

      <header className="app-header">
        <button
          type="button"
          className="nav-toggle btn btn--ghost btn--sm"
          aria-expanded={navOpen}
          aria-controls="app-nav"
          onClick={() => setNavOpen((v) => !v)}
        >
          <span aria-hidden="true">☰</span>
          <span className="visually-hidden">{navOpen ? 'Hide navigation' : 'Show navigation'}</span>
        </button>

        {/* Where am I: organisation, then the entity inside it. */}
        <div className="app-context">
          <span className="app-context__org">{branding.organisationName}</span>
          {user.entities.length > 1 ? (
            <label className="app-context__entity">
              <span className="visually-hidden">{t.entity} you are working in</span>
              <select
                className="entity-select"
                value={user.entityId}
                onChange={(e) => {
                  void switchEntity(e.target.value).then(() => navigate('/'));
                }}
              >
                {user.entities.map((entity) => (
                  <option key={entity.entityId} value={entity.entityId}>
                    {entity.entityName} ({entity.entityCode})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="app-context__entity app-context__entity--single">
              {activeEntity?.entityName}
              {activeEntity?.entityCode ? <span className="muted"> ({activeEntity.entityCode})</span> : null}
            </span>
          )}
        </div>

        <div className="app-header__right">
          {/* Where are we in the calendar. */}
          {/*
            Always rendered, so the bar does not jump sideways when the figures
            arrive a moment after the frame.
          */}
          <div className="app-period" title={year?.startDate ? `${date(year.startDate)} to ${date(year.endDate)}` : undefined}>
            <span className="app-period__label">Financial year</span>
            <span className="app-period__value">
              {year?.code ?? ' '}
              {currentPeriod ? <span className="app-period__period"> · {currentPeriod.name}</span> : null}
            </span>
          </div>

          <NavLink to="/approvals" className="icon-btn" aria-label={`My approvals${waiting > 0 ? `, ${waiting} waiting` : ', none waiting'}`}>
            <span aria-hidden="true">◔</span>
            {waiting > 0 ? (
              <span className="icon-btn__count" aria-hidden="true">
                {waiting > 99 ? '99+' : waiting}
              </span>
            ) : null}
          </NavLink>

          <div className="app-menu" ref={menu === 'help' ? menuRef : undefined}>
            <button
              type="button"
              className="icon-btn"
              aria-expanded={menu === 'help'}
              aria-haspopup="dialog"
              aria-label="Help and support"
              onClick={() => setMenu((m) => (m === 'help' ? 'none' : 'help'))}
            >
              <span aria-hidden="true">?</span>
            </button>
            {menu === 'help' ? (
              <div className="popover popover--wide" role="dialog" aria-label="Help and support">
                <p className="popover__title">Help and support</p>
                <p className="smaller muted">
                  {branding.productName} is {branding.productTagline.toLowerCase()} supplied by Altari Systems.
                  {isDemonstration ? ` This is a ${environmentLabel.toLowerCase()} holding synthetic data.` : ''}
                </p>
                {support ? (
                  <dl className="definition-list smaller" style={{ marginTop: '0.75rem' }}>
                    <div>
                      <dt>Service desk</dt>
                      <dd>{support.deskName}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{support.email}</dd>
                    </div>
                    <div>
                      <dt>Telephone</dt>
                      <dd>{support.telephone}</dd>
                    </div>
                    <div>
                      <dt>Hours</dt>
                      <dd>{support.hours}</dd>
                    </div>
                  </dl>
                ) : null}
                {canAny('support.raise') ? (
                  <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                    <NavLink to="/support" className="btn btn--sm" onClick={() => setMenu('none')}>
                      Open the support centre
                    </NavLink>
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="app-menu" ref={menu === 'profile' ? menuRef : undefined}>
            <button
              type="button"
              className="profile-btn"
              aria-expanded={menu === 'profile'}
              aria-haspopup="menu"
              onClick={() => setMenu((m) => (m === 'profile' ? 'none' : 'profile'))}
            >
              <span className="profile-btn__avatar" aria-hidden="true">
                {initials}
              </span>
              <span className="profile-btn__name">{user.fullName}</span>
              <span className="profile-btn__caret" aria-hidden="true">
                ▾
              </span>
            </button>
            {menu === 'profile' ? (
              <div className="popover" role="menu" aria-label="Your account">
                <p className="popover__title">{user.fullName}</p>
                {user.jobTitle ? <p className="smaller muted">{user.jobTitle}</p> : null}
                <p className="smaller muted" style={{ marginTop: '0.35rem' }}>
                  {activeEntity?.entityName}
                </p>
                {user.roles?.length ? (
                  <p className="smaller muted">{[...new Set(user.roles.map((r) => r.name))].join(', ')}</p>
                ) : null}
                <hr className="popover__rule" />
                <button
                  type="button"
                  className="btn btn--block btn--sm"
                  role="menuitem"
                  onClick={() => {
                    setMenu('none');
                    void signOut().then(() => navigate('/'));
                  }}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {navOpen ? <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" /> : null}

      <nav className={navOpen ? 'app-nav is-open' : 'app-nav'} id="app-nav" aria-label="Sections">
        {visible.map((group) => (
          <div className="nav-group" key={group.label}>
            <p className="nav-group__label">{group.label}</p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? 'nav-link is-active' : 'nav-link')}
                onClick={() => setNavOpen(false)}
              >
                <span className="nav-link__icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="nav-link__label">{item.label}</span>
                {item.badge ? (
                  <span className="nav-link__badge">
                    {item.badge}
                    <span className="visually-hidden"> waiting</span>
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
        <p className="nav-footer">
          {branding.providerLine ?? 'Powered by Altari Systems'}
          {user.entities.length > 1 ? (
            <>
              <br />
              {user.entities.length} {user.entities.length === 1 ? t.entity.toLowerCase() : t.entityPlural.toLowerCase()} available
              to you
            </>
          ) : null}
        </p>
      </nav>

      <main className="app-main" id="main" tabIndex={-1}>
        <div className="page">
          {isDemonstration ? (
            <DemonstrationNotice label={environmentLabel} organisation={branding.organisationName} />
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
}

/** Guard a route by permission, with an honest refusal rather than a blank page. */
export function RequirePermission({ anyOf, children }: { anyOf: string[]; children: ReactNode }) {
  const { canAny } = useSession();
  if (!canAny(...anyOf)) {
    return (
      <div className="card">
        <div className="card__body">
          <h1>You do not have access to this</h1>
          <p className="muted">
            Your roles do not include the permission this screen needs. Access is granted by role, and the server checks
            it on every request — so this is not a display choice, it is the answer.
          </p>
          <p className="muted smaller">
            If you believe you should have access, ask your system administrator to review your roles.
          </p>
          <details>
            <summary className="smaller">Technical detail</summary>
            <p className="smaller muted mono">Requires any one of: {anyOf.join(', ')}</p>
          </details>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
